import type { QuestionnaireAnswers } from '@aikoa/common'
import { getFeedbackQuestionnaireData, isDeclinedQuestionnaireAnswers } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useCallback, useMemo } from 'react'
import { Platform, View } from 'react-native'
import Animated, { FadeInUp, FadeOutUp } from 'react-native-reanimated'
import { ValueList, type ValueListProps, type ValueItem } from '~/components/layout/ValueList'
import { Label } from '~/components/typography/Content'
import { cn } from '~/lib/utils'

const isWeb = Platform.OS === 'web'

export default function FeedbackAnswersView({
  answers,
  answersClassName,
  className,
  itemClassName,
  itemContentClassName,
  valueClassName,
  labelClassName,
  variant = 'default',
}: Omit<ValueListProps, 'values'> & {
  answersClassName?: string
  answers: QuestionnaireAnswers | null
  variant?: 'default' | 'compact'
}) {
  const { t } = useTranslations('feedback')
  const questionnaire = getFeedbackQuestionnaireData(t)

  const getOptionLabel = useCallback(
    (questionId: string, value: number | string): string | undefined => {
      const question = questionnaire.questions.find((q) => q.id === questionId)

      if (question && question.type === 'multi-choice') {
        const numericValue = typeof value === 'number' ? value : Number(value)

        if (Number.isFinite(numericValue)) {
          return question.config.options.find((option) => option.value === numericValue)?.label
        }
      }
    },
    [questionnaire.questions]
  )

  const questionsToHide = useMemo(() => {
    return new Set<string>()
  }, [])

  const valueItems: ValueItem[] = useMemo(() => {
    if (
      !answers ||
      isDeclinedQuestionnaireAnswers(answers) ||
      (Array.isArray(answers) && answers.length === 0)
    ) {
      return []
    }

    const items: ValueItem[] = []

    for (const question of questionnaire.questions) {
      if (questionsToHide.has(question.id)) {
        continue
      }

      const answerEntry =
        Array.isArray(answers) && answers.find((answer) => answer.id === question.id)
      const answerValue = answerEntry && answerEntry.value

      const hasEmptyAnswer = answerValue === undefined || answerValue === ''

      if (hasEmptyAnswer) {
        continue
      }

      const displayValue =
        question.type === 'multi-choice'
          ? getOptionLabel(question.id, answerValue as string | number)
          : String(answerValue)

      items.push({
        value: question.question,
        description: displayValue,
      })
    }

    return variant === 'compact' ? items.slice(0, 1) : items
  }, [answers, variant, questionnaire.questions, questionsToHide, t, getOptionLabel])

  const isEmptyOrDeclined =
    !answers ||
    isDeclinedQuestionnaireAnswers(answers) ||
    (Array.isArray(answers) && answers.length === 0)

  return (
    <Animated.View
      className={className}
      entering={FadeInUp.duration(150)}
      exiting={isWeb ? undefined : FadeOutUp.duration(150)}>
      {isEmptyOrDeclined ? (
        <View className="items-center justify-center rounded-lg bg-muted px-6 py-8">
          <Label className="text-pretty text-center text-muted-foreground">
            {isDeclinedQuestionnaireAnswers(answers ?? [])
              ? t(($) => $.answers.declined)
              : t(($) => $.answers.noAnswers)}
          </Label>
        </View>
      ) : (
        <ValueList
          className={cn(answersClassName)}
          values={valueItems}
          itemClassName={cn('gap-4', itemClassName)}
          itemContentClassName={cn('gap-2', itemContentClassName)}
          labelClassName={cn('font-light text-sm', labelClassName)}
          valueClassName={cn('font-semibold text-base', valueClassName)}
        />
      )}
    </Animated.View>
  )
}
